import { type SVGProps } from 'react';

// prototype-v2 스트로크 아이콘 세트 (1.8px stroke) — 이모지 대신 사용한다.
// 출처: /prototype-v2/index.html SVG sprite
export type IconName =
  | 'back'
  | 'bell'
  | 'home'
  | 'compass'
  | 'users'
  | 'user'
  | 'plus'
  | 'check'
  | 'up'
  | 'right'
  | 'pin'
  | 'cal'
  | 'coin'
  | 'edit'
  | 'menu'
  | 'target'
  | 'chat'
  | 'clock'
  | 'map'
  | 'heart'
  | 'receipt'
  | 'camera'
  | 'x'
  | 'live'
  | 'vote'
  | 'wallet'
  | 'lock'
  | 'mail';

const STROKE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

const PATHS: Record<IconName, JSX.Element> = {
  back: <path d="M15 5l-7 7 7 7" {...STROKE} />,
  bell: (
    <path d="M18 9a6 6 0 10-12 0c0 5-2 6-2 6h16s-2-1-2-6M10.5 19a1.8 1.8 0 003 0" {...STROKE} />
  ),
  home: <path d="M4 10.5L12 4l8 6.5V20h-5.5v-5h-5v5H4z" {...STROKE} />,
  compass: (
    <>
      <circle cx="12" cy="12" r="8.5" {...STROKE} />
      <path d="M15 9l-2 4-4 2 2-4z" {...STROKE} />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8.5" r="3.2" {...STROKE} />
      <path
        d="M3.5 19c.6-3 2.8-4.5 5.5-4.5s4.9 1.5 5.5 4.5M15.5 5.8a3.2 3.2 0 010 5.4M17.5 14.8c1.7.6 2.7 1.9 3 4.2"
        {...STROKE}
      />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8.5" r="3.5" {...STROKE} />
      <path d="M5 19.5c.8-3.5 3.6-5 7-5s6.2 1.5 7 5" {...STROKE} />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" {...STROKE} strokeWidth={2} />,
  check: <path d="M5 12.5l4.5 4.5L19 7.5" {...STROKE} strokeWidth={2.2} />,
  up: <path d="M12 19V5m-6 6l6-6 6 6" {...STROKE} strokeWidth={2} />,
  right: <path d="M5 12h14m-6-6l6 6-6 6" {...STROKE} />,
  pin: (
    <>
      <path d="M12 21s-6.5-5.4-6.5-10.2a6.5 6.5 0 1113 0C18.5 15.6 12 21 12 21z" {...STROKE} />
      <circle cx="12" cy="10.5" r="2.2" {...STROKE} />
    </>
  ),
  cal: (
    <>
      <rect x="4" y="6" width="16" height="14" rx="3" {...STROKE} />
      <path d="M4 10.5h16M8.5 4v3.5M15.5 4v3.5" {...STROKE} />
    </>
  ),
  coin: (
    <>
      <circle cx="12" cy="12" r="8.5" {...STROKE} />
      <path d="M9 9.5h6M9 12.5h6M11 7l1.5 9" {...STROKE} strokeWidth={1.6} />
    </>
  ),
  edit: <path d="M14.5 5.5l4 4L8 20H4v-4zM12.5 7.5l4 4" {...STROKE} />,
  menu: <path d="M4 7h16M4 12h16M4 17h16" {...STROKE} />,
  target: (
    <>
      <circle cx="12" cy="12" r="7" {...STROKE} />
      <circle cx="12" cy="12" r="2.2" fill="currentColor" stroke="none" />
      <path d="M12 2.5v4M12 17.5v4M2.5 12h4M17.5 12h4" {...STROKE} />
    </>
  ),
  chat: (
    <path d="M20 12a8 8 0 11-3.1-6.3A8 8 0 0120 12zM7 21l1.5-3.5" {...STROKE} />
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" {...STROKE} />
      <path d="M12 7.5V12l3 2" {...STROKE} />
    </>
  ),
  map: <path d="M9 5L4 7v12l5-2 6 2 5-2V5l-5 2zM9 5v12M15 7v12" {...STROKE} />,
  heart: (
    <path
      d="M12 20s-7-4.6-7-10a4.2 4.2 0 017-3.1A4.2 4.2 0 0119 10c0 5.4-7 10-7 10z"
      {...STROKE}
    />
  ),
  receipt: (
    <path
      d="M6 3.5h12V21l-2-1.4-2 1.4-2-1.4-2 1.4-2-1.4L6 21zM9 8h6M9 11.5h6M9 15h4"
      {...STROKE}
    />
  ),
  camera: (
    <>
      <path d="M4 8.5h3l1.5-2.5h7L17 8.5h3V19H4z" {...STROKE} />
      <circle cx="12" cy="13.5" r="3" {...STROKE} />
    </>
  ),
  x: <path d="M6 6l12 12M18 6L6 18" {...STROKE} />,
  live: (
    <>
      <circle cx="12" cy="12" r="2.4" fill="currentColor" stroke="none" />
      <path d="M7.5 7.5a6.4 6.4 0 000 9M16.5 7.5a6.4 6.4 0 010 9" {...STROKE} />
    </>
  ),
  vote: (
    <path d="M6 5.5H18M6 10H18M6 14.5H13M4 5.5H4.01M4 10H4.01M4 14.5H4.01" {...STROKE} />
  ),
  wallet: (
    <>
      <rect x="4" y="6" width="16" height="13" rx="3" {...STROKE} />
      <path d="M16 11.5H20V16H16C14.8 16 14 15.1 14 13.8C14 12.5 14.8 11.5 16 11.5Z" {...STROKE} />
      <path d="M7 6V4.5H16V6" {...STROKE} />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="10" width="14" height="10" rx="2.5" {...STROKE} />
      <path d="M8 10V7.5C8 5.3 9.6 3.8 12 3.8C14.4 3.8 16 5.3 16 7.5V10" {...STROKE} />
    </>
  ),
  mail: (
    <>
      <rect x="4" y="6" width="16" height="12" rx="2.5" {...STROKE} />
      <path d="M5 8L12 13L19 8" {...STROKE} />
    </>
  ),
};

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  size?: number;
}

export function Icon({ name, size = 18, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}
